<template>
  <div v-if="headings.length > 0" class="p-content-sidebar">
    <nav class="p-content-sidebar__nav">
      <ul class="p-content-sidebar__list">
        <!-- Content Title -->
        <li v-if="title" class="p-content-sidebar__item">
          <span class="p-content-sidebar__link f-utility-14-medium t-fg-primary">
            {{ title }}
          </span>
        </li>

        <!-- Headings Navigation -->
        <li v-for="(heading, index) in headings" :key="index" class="p-content-sidebar__item">
          <a
            :href="`#${heading.id}`"
            class="p-content-sidebar__link f-utility-14-medium t-fg-secondary"
            :class="{ 'p-content-sidebar__link--active': activeHeading === heading.id }"
            @click.prevent="handleHeadingClick(heading.id)"
          >
            {{ heading.text }}
          </a>
        </li>
      </ul>
    </nav>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, nextTick, watch } from 'vue'

interface Heading {
  id: string
  text: string
  element: HTMLElement
}

const props = defineProps<{
  content: string
  title?: string
}>()

const headings = ref<Heading[]>([])
const activeHeading = ref<string>('')

const extractHeadings = () => {
  const parser = new DOMParser()
  const doc = parser.parseFromString(props.content, 'text/html')
  const h2Elements = doc.querySelectorAll('h2')

  const cleaned: Heading[] = []
  Array.from(h2Elements).forEach((h2) => {
    const raw = h2.textContent || ''
    const text = raw.replace(/[\u00A0\u200B-\u200D\uFEFF]/g, '').trim()
    if (!text) return

    const id = h2.id || `heading-${cleaned.length}`
    cleaned.push({ id, text, element: h2 as HTMLElement })
  })

  headings.value = cleaned
}

const addIdsToHeadings = () => {
  nextTick(() => {
    const contentBody = document.querySelector('.content-details__body')
    if (contentBody) {
      const h2Elements = contentBody.querySelectorAll('h2')
      h2Elements.forEach((h2, index) => {
        if (!h2.id) {
          h2.id = `heading-${index}`
        }
      })
    }
  })
}

const waitForElementById = (
  id: string,
  maxAttempts = 20,
  intervalMs = 50,
): Promise<HTMLElement | null> => {
  return new Promise((resolve) => {
    let attemptsRemaining = maxAttempts
    const tryFind = () => {
      const el = document.getElementById(id)
      if (el) return resolve(el)
      if (attemptsRemaining <= 0) return resolve(null)
      attemptsRemaining -= 1
      setTimeout(tryFind, intervalMs)
    }
    tryFind()
  })
}

const handleHeadingClick = async (headingId: string) => {
  // Ensure IDs are present in rendered DOM
  addIdsToHeadings()
  await nextTick()

  const element = document.getElementById(headingId) || (await waitForElementById(headingId))
  if (!element) return
  const headerEl = document.querySelector('.header') as HTMLElement | null
  const headerHeight = headerEl?.offsetHeight ?? 0

  const targetY = window.scrollY + element.getBoundingClientRect().top - (headerHeight + 8)

  window.scrollTo({
    top: targetY,
    behavior: 'smooth',
  })

  activeHeading.value = headingId
}

onMounted(() => {
  extractHeadings()
  addIdsToHeadings()
})

watch(
  () => props.content,
  () => {
    extractHeadings()
    addIdsToHeadings()
  },
)
</script>
